/* eslint-disable @typescript-eslint/no-explicit-any */
import isNil from 'lodash/isNil';

import type { fhirR4 } from '@smile-cdr/fhirts';
import { ExtensionService } from '../services/extension.service';

export const answerTypeConverter = {
  attachment: (value: any) => value,

  boolean: (value: any, originExtension: fhirR4.Extension[]) => [
    { valueBoolean: value, extension: originExtension },
  ],

  choice: (
    value: any,
    originExtension: fhirR4.Extension[],
    qItem: fhirR4.QuestionnaireItem,
    qrItem: fhirR4.QuestionnaireResponseItem,
  ) =>
    ConverterHelper.convertCodesToValueCodingList(
      qItem.answerOption,
      value,
      qrItem,
      originExtension,
    ),

  date: (value: any, originExtension: fhirR4.Extension[]) => {
    try {
      const valueDate = new Date(value)?.toISOString().split('T')[0];
      return [{ valueDate, extension: originExtension }];
    } catch (err) {
      console.log('Error parsing date', err);
      return [];
    }
  },

  dateTime: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfEmpty(
      new Date(value).toJSON(),
      'valueDateTime',
      originExtension,
    ),

  decimal: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfValidNumber(
      Number.parseFloat(value),
      'valueDecimal',
      originExtension,
    ),

  integer: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfValidNumber(
      Number.parseInt(value, 10),
      'valueInteger',
      originExtension,
    ),

  'open-choice': (
    value: any,
    originExtension: fhirR4.Extension[],
    qItem: fhirR4.QuestionnaireItem,
    qrItem: fhirR4.QuestionnaireResponseItem,
  ) => {
    const label = qrItem.answer?.find((initAns) => initAns.valueString);
    if (typeof value === 'string') {
      return ConverterHelper.createOrUpdateLabelString(
        qrItem,
        value,
        label,
        originExtension,
      );
    }
    return ConverterHelper.updateOpenChoice(
      qItem,
      value,
      label,
      qrItem,
      originExtension,
    );
  },

  quantity: (
    value: any,
    originExtension: fhirR4.Extension[],
    qItem: fhirR4.QuestionnaireItem,
    qrItem: fhirR4.QuestionnaireResponseItem,
    code?: string,
  ) => {
    const prevQuantity = qrItem?.answer?.[0]?.valueQuantity || {};
    if (isNil(code)) {
      return ConverterHelper.checkIfValidQuantity(
        value,
        originExtension,
        prevQuantity,
      );
    } else {
      return [
        {
          extension: originExtension,
          valueQuantity: {
            ...prevQuantity,
            ...ConverterHelper.parseUnit(qItem, code),
          },
        },
      ];
    }
  },

  reference: (value: any, originExtension: fhirR4.Extension[]) => {
    if (typeof value === 'string')
      return [
        { valueReference: { reference: value }, extension: originExtension },
      ];
    else {
      return value.map((reference) => {
        return { valueReference: { reference }, extension: originExtension };
      });
    }
  },

  string: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfEmpty(
      value.toString(),
      'valueString',
      originExtension,
    ),

  text: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfEmpty(
      value.toString(),
      'valueString',
      originExtension,
    ),

  time: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfEmpty(value, 'valueTime', originExtension),

  url: (value: any, originExtension: fhirR4.Extension[]) =>
    ConverterHelper.checkIfEmpty(value, 'valueUri', originExtension),
};

function convertCodesToValueCodingList(
  options: fhirR4.QuestionnaireAnswerOption[] | undefined,
  value: any,
  qrItem: fhirR4.QuestionnaireResponseItem,
  originExtension: fhirR4.Extension[],
) {
  if (typeof value === 'string') value = [value];
  const existingAns =
    qrItem?.answer?.filter((answer) =>
      value.includes(answer.valueCoding?.code),
    ) || [];
  const existingCodes = existingAns?.map((answer) => answer.valueCoding?.code);
  const newCodes = value.filter((code) => !existingCodes?.includes(code));
  return existingAns.concat(
    options
      ?.filter((x: fhirR4.QuestionnaireAnswerOption) =>
        newCodes.includes(x.valueCoding?.code),
      )
      .map((valueCoding) => {
        return {
          ...valueCoding,
          extension: originExtension,
        };
      }) || [],
  );
}

function updateOpenChoice(
  qItem: fhirR4.QuestionnaireItem,
  value: string[],
  label: fhirR4.QuestionnaireResponseAnswer | undefined,
  qrItem: fhirR4.QuestionnaireResponseItem,
  originExtension: fhirR4.Extension[],
) {
  const retVal = ConverterHelper.convertCodesToValueCodingList(
    qItem.answerOption?.concat({ valueCoding: { code: 'openLabel' } }),
    value,
    qrItem,
    originExtension,
  );
  if (value.includes('openLabel') && label) {
    return retVal.concat(label);
  }
  return retVal;
}

function createOrUpdateLabelString(
  qrItem: fhirR4.QuestionnaireResponseItem,
  value: string,
  label: fhirR4.QuestionnaireResponseAnswer | undefined,
  originExtension: fhirR4.Extension[],
) {
  const actualOriginExtension =
    ExtensionService.updateOriginExtensionIfNecessary(label, originExtension);

  if (label) {
    label.valueString = value;
    label.extension = value ? actualOriginExtension : undefined;
  } else {
    qrItem.answer = qrItem.answer?.concat({
      valueString: value,
      extension: actualOriginExtension,
    });
  }
  return qrItem.answer;
}

function checkIfEmpty(value: any, label: string, origin: fhirR4.Extension[]) {
  if (value) {
    const answer = { extension: origin };
    answer[label] = value;
    return [answer];
  } else return [];
}

function checkIfValidNumber(
  value: any,
  label: string,
  origin: fhirR4.Extension[],
) {
  if (!Number.isNaN(value)) {
    const answer = { extension: origin };
    answer[label] = value;
    return [answer];
  } else return [];
}

function checkIfValidQuantity(
  value: any,
  origin: fhirR4.Extension[],
  prevQuantity?: any,
) {
  const numericValue = Number.parseFloat(value);
  if (!Number.isNaN(numericValue) || value === '') {
    prevQuantity.value = value === '' ? undefined : numericValue;
    return [{ extension: origin, valueQuantity: prevQuantity }];
  } else return [];
}

function parseUnit(qItem: fhirR4.QuestionnaireItem, code: string) {
  const unitOption = qItem.extension
    ?.filter((ext) => ext.url === ExtensionService.unitOptionExtensionUrl)
    .map((ext) => ext.valueCoding)
    ?.filter((option) => option?.code === code)
    .pop();
  return {
    code: unitOption?.code,
    unit: unitOption?.display,
    system: unitOption?.system,
  };
}

/* This class is necessary for mocking the functions in unit tests */
export abstract class ConverterHelper {
  static readonly convertCodesToValueCodingList = convertCodesToValueCodingList;
  static readonly updateOpenChoice = updateOpenChoice;
  static readonly createOrUpdateLabelString = createOrUpdateLabelString;
  static readonly checkIfEmpty = checkIfEmpty;
  static readonly checkIfValidNumber = checkIfValidNumber;
  static readonly checkIfValidQuantity = checkIfValidQuantity;
  static readonly parseUnit = parseUnit;
}
